/**
 * Palantir PMS - Software para la gestion de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creacion / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso esta restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Reservations from "./Reservations";
import ReservationsWaitlist from "./ReservationsWaitlist";
import AvailabilitySummary from "./AvailabilitySummary";
import ClientAllocations from "../Components/ClientAllocations";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { readStoredUser } from "../utils/sessionUser";
import { isDemoMode } from "../demo/demoMode";
import { canAccessReservationsWaitlist } from "../demo/demoAccessPolicy";
import "../RoomsManagement.css";

const ReservationsTabs = ({ forcedTab = null }) => {
  const [currentUser, setCurrentUser] = useState(() => readStoredUser());
  const permissions = currentUser?.permissions || {};
  const isAdminRole = Number(currentUser?.id) === 1;
  const canViewRoomAssignment = Boolean(
    isAdminRole || permissions["rooms.view_assignment"]
  );

  const canViewReservations = Boolean(
    isAdminRole ||
      permissions["reservations.view_list"] ||
      permissions["reservations.create"] ||
      canViewRoomAssignment
  );
  const canViewWaitlist = canAccessReservationsWaitlist({
    canViewReservations,
    demoModeActive: isDemoMode(),
  });
  const canViewAvailability = Boolean(
    isAdminRole || permissions["reservations.view_availability"]
  );
  const canViewAllocations = canViewAvailability && !isDemoMode();

  const firstAllowedTab = useMemo(() => {
    if (canViewReservations) return "reservations";
    if (canViewAvailability) return "availability";
    if (canViewAllocations) return "allocations";
    return "reservations";
  }, [canViewAllocations, canViewAvailability, canViewReservations]);

  const isAllowedTab = useMemo(
    () => (tab) => {
      if (tab === "reservations") return canViewReservations;
      if (tab === "waitlist") return canViewWaitlist;
      if (tab === "availability") return canViewAvailability;
      if (tab === "allocations") return canViewAllocations;
      return false;
    },
    [canViewAllocations, canViewAvailability, canViewReservations, canViewWaitlist]
  );

  const [activeTab, setActiveTab] = useState(
    forcedTab && isAllowedTab(forcedTab) ? forcedTab : firstAllowedTab
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const savedStatus = searchParams.get("saved_status");
  const { themeId, language } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const tabsCopy = language.messages.reservationsTabs;

  useEffect(() => {
    const syncSessionFromStorage = () => setCurrentUser(readStoredUser());
    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    window.addEventListener("storage", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
      window.removeEventListener("storage", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    if (!savedStatus) return undefined;

    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("saved_status");
      setSearchParams(next);
    }, 3000);

    return () => clearTimeout(timer);
  }, [savedStatus, searchParams, setSearchParams]);

  useEffect(() => {
    if (forcedTab && isAllowedTab(forcedTab)) {
      setActiveTab(forcedTab);
      return;
    }

    setActiveTab(firstAllowedTab);
  }, [firstAllowedTab, forcedTab, isAllowedTab]);

  if (!canViewReservations && !canViewAvailability && !canViewAllocations) {
    return (
      <div
        className={`text-center mt-4 fs-5 ${isLightTheme ? "text-dark" : "text-white"}`}
      >
        {tabsCopy.noPermission}
      </div>
    );
  }

  const showInlineTabs = !forcedTab;
  const tabsLayoutCss = `
    .reservations-tabs-container {
      height: 100%;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .reservations-tabs-container .tab-content {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .reservations-tabs-container .tab-content > * {
      flex: 1 1 auto;
      min-height: 0;
    }
  `;

  const tabsThemeCss = isLightTheme
    ? `
        .reservations-tabs-container--light,
        .reservations-tabs-container--light .tab-content {
          color: #26354f;
        }

        .reservations-tabs-container--light .alert-success {
          background: #e7f6f2;
          border: 1px solid #bfe3da;
          color: #216b5a;
        }
      `
    : `
        .reservations-tabs-container .alert-success {
          background: #121f3d;
          border: 1px solid rgba(99, 193, 199, 0.38);
          color: #a9e1da;
        }
      `;

  const getTabButtonStyle = (isActive) => ({
    background: isActive
      ? isLightTheme
        ? "#ffffff"
        : "rgba(61, 182, 233, 0.12)"
      : "transparent",
    border: isActive
      ? isLightTheme
        ? "1px solid #bdcad8"
        : "1px solid rgba(61, 182, 233, 0.48)"
      : "1px solid transparent",
    borderBottom: isActive
      ? isLightTheme
        ? "1px solid #bdcad8"
        : "1px solid rgba(61, 182, 233, 0.48)"
      : "1px solid transparent",
    borderRadius: "10px",
    padding: "9px 16px",
    color: isActive
      ? isLightTheme
        ? "#223a7a"
        : "#b9e8f5"
      : isLightTheme
        ? "#5b6680"
        : "#dce8fa",
    fontWeight: 800,
    boxShadow: isActive ? "inset 3px 0 0 #3db6e9" : "none",
  });

  const tabsRowStyle = isLightTheme
    ? {
        gap: "8px",
        width: "fit-content",
        padding: "7px",
        borderRadius: "12px",
        border: "1px solid #d7e2ec",
        background: "#f7fafc",
        boxShadow: "0 10px 28px rgba(34, 58, 122, 0.08)",
      }
    : {
        gap: "8px",
        width: "fit-content",
        padding: "7px",
        border: "1px solid rgba(99, 193, 199, 0.2)",
        borderRadius: "12px",
        background: "#121f3d",
        boxShadow: "0 14px 32px rgba(2, 8, 28, 0.28)",
      };

  return (
    <div
      className={`reservations-tabs-container ${
        isLightTheme ? "reservations-tabs-container--light" : ""
      }`}
      style={{ borderBottom: "none" }}
    >
      <style>{`${tabsLayoutCss}\n${tabsThemeCss}`}</style>
      {savedStatus && (
        <div
          className="alert alert-success fw-bold text-center"
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1050,
            width: "fit-content",
            maxWidth: "90%",
            padding: "10px 20px",
            borderRadius: "8px",
            fontSize: "0.95rem",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            color: isLightTheme ? "#216b5a" : undefined,
            borderColor: isLightTheme ? "#bfe3da" : undefined,
          }}
        >
          {tabsCopy.savedSuccess}
        </div>
      )}

      {showInlineTabs && (
        <div className="tabs d-flex mb-3" style={tabsRowStyle}>
          {canViewReservations && (
            <button
              onClick={() => setActiveTab("reservations")}
            style={getTabButtonStyle(activeTab === "reservations")}
          >
              {tabsCopy.listOfReservations}
            </button>
          )}

          {canViewWaitlist && (
            <button
              onClick={() => setActiveTab("waitlist")}
              style={getTabButtonStyle(activeTab === "waitlist")}
            >
              {tabsCopy.waitlist}
            </button>
          )}

          {canViewAvailability && (
            <button
              onClick={() => setActiveTab("availability")}
            style={getTabButtonStyle(activeTab === "availability")}
          >
              {tabsCopy.reservationsAvailability}
            </button>
          )}

          {canViewAllocations && (
            <button
              onClick={() => setActiveTab("allocations")}
            style={getTabButtonStyle(activeTab === "allocations")}
          >
              {tabsCopy.categorySalesControl}
            </button>
          )}
        </div>
      )}

      <div className={`tab-content ${showInlineTabs ? "mt-3" : ""}`}>
        {activeTab === "reservations" && canViewReservations && <Reservations />}
        {activeTab === "waitlist" && canViewWaitlist && <ReservationsWaitlist />}
        {activeTab === "availability" && canViewAvailability && <AvailabilitySummary />}
        {activeTab === "allocations" && canViewAllocations && <ClientAllocations />}
      </div>
    </div>
  );
};

export default ReservationsTabs;
